
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import type { Payment } from "@/api/payments"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface EditPaymentDateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  payment: Payment
  onDateChanged: (paymentId: string, newDate: string) => void
}

export function EditPaymentDateDialog({
  open,
  onOpenChange,
  payment,
  onDateChanged,
}: EditPaymentDateDialogProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [date, setDate] = useState("")

  useEffect(() => {
    if (open && payment) {
      // Convert payment date to YYYY-MM-DD for the input
      try {
        const d = new Date(payment.date)
        setDate(d.toISOString().split("T")[0])
      } catch {
        setDate(new Date().toISOString().split("T")[0])
      }
    }
  }, [open, payment])

  const getUserName = () => {
    if (typeof payment.userId === "object" && payment.userId !== null) {
      const user = payment.userId
      return `${user.name} ${user.surname || ""}`.trim()
    }
    return "Unknown User"
  }

  const getPlanName = () => {
    if (typeof payment.planId === "object" && payment.planId !== null) {
      return payment.planId.name
    }
    return "Unknown Plan"
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!date) return

    setIsLoading(true)
    try {
      onDateChanged(payment._id || "", date)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("payments.editDate", "Edit Payment Date")}</DialogTitle>
          <DialogDescription>
            {t("payments.editDateDescription", "Change the start date of this payment. The end date will be recalculated automatically.")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">{t("payments.user", "User")}</Label>
                <div className="font-medium">{getUserName()}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t("payments.plan", "Plan")}</Label>
                <div className="font-medium">{getPlanName()}</div>
              </div>
            </div>
            <div>
              <Label htmlFor="payment-date">{t("payments.date", "Date")}</Label>
              <Input
                id="payment-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading || !date}>
              {isLoading ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
